// ============================================
// InvoiceModal — solicita los datos fiscales del receptor y dispara la
// emisión vía Facturapi. Persiste el último receptor usado en localStorage
// para que la próxima factura ya venga precargada.
// ============================================

import React, { useEffect, useState } from 'react';
import { requestInvoice } from '../../services/medusa/distributor';
import './InvoiceModal.css';

const RECEPTOR_STORAGE_KEY = 'phys_invoice_receptor';

// Régimenes fiscales SAT más comunes para distribuidoras y consumidores PF/PM.
// La lista completa es larga; aquí los relevantes para el caso de uso.
const TAX_SYSTEMS = [
  { code: '626', label: '626 · Régimen Simplificado de Confianza (RESICO)' },
  { code: '612', label: '612 · Personas Físicas con Actividad Empresarial' },
  { code: '601', label: '601 · General de Ley Personas Morales' },
  { code: '603', label: '603 · Personas Morales con Fines no Lucrativos' },
  { code: '605', label: '605 · Sueldos y Salarios' },
  { code: '606', label: '606 · Arrendamiento' },
  { code: '616', label: '616 · Sin obligaciones fiscales' },
];

// Usos CFDI 4.0 frecuentes en retail.
const CFDI_USES = [
  { code: 'G03', label: 'G03 · Gastos en general' },
  { code: 'G01', label: 'G01 · Adquisición de mercancías' },
  { code: 'P01', label: 'P01 · Por definir' },
  { code: 'S01', label: 'S01 · Sin efectos fiscales' },
];

const DEFAULT_FORM = {
  legal_name: '',
  tax_id: '',
  tax_system: '626',
  email: '',
  zip: '',
  use: 'G03',
};

export default function InvoiceModal({ open, orderId, token, onClose, onSuccess }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Precarga el último receptor si existe (mismo dispositivo).
  useEffect(() => {
    if (!open) return;
    try {
      const saved = JSON.parse(localStorage.getItem(RECEPTOR_STORAGE_KEY) || 'null');
      if (saved) setForm({ ...DEFAULT_FORM, ...saved });
    } catch (e) { /* ignore */ }
    setError(null);
  }, [open]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validación mínima · RFC mexicano: 12 (PM) o 13 (PF) caracteres alfanuméricos.
    const rfc = form.tax_id.toUpperCase().trim();
    if (!/^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/.test(rfc)) {
      setError('RFC inválido. Verifica formato (ej. XAXX010101000).');
      return;
    }
    if (!/^\d{5}$/.test(form.zip)) {
      setError('Código postal inválido (5 dígitos).');
      return;
    }
    if (!form.legal_name.trim()) {
      setError('La razón social es obligatoria.');
      return;
    }

    setSubmitting(true);
    try {
      const { invoice } = await requestInvoice(token, orderId, {
        receptor: {
          legal_name: form.legal_name.trim(),
          tax_id: rfc,
          tax_system: form.tax_system,
          email: form.email.trim(),
          address: { zip: form.zip.trim() },
        },
        use: form.use,
        payment_form: '99',
        payment_method: 'PUE',
      });
      // Guardamos para precargar la próxima vez.
      try {
        localStorage.setItem(
          RECEPTOR_STORAGE_KEY,
          JSON.stringify({ ...form, tax_id: rfc })
        );
      } catch (e) { /* ignore */ }
      onSuccess?.(invoice);
    } catch (err) {
      setError(err?.message || 'No fue posible enviar tu solicitud. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="invmodal__overlay" onClick={onClose}>
      <div
        className="invmodal__card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invmodal-title"
      >
        <header className="invmodal__head">
          <h2 id="invmodal-title" className="invmodal__title">
            Solicitar factura
          </h2>
          <button
            className="invmodal__close"
            onClick={onClose}
            aria-label="Cerrar"
            type="button"
          >
            ✕
          </button>
        </header>

        <form className="invmodal__form" onSubmit={handleSubmit}>
          <label className="invmodal__field">
            <span>Razón social</span>
            <input
              type="text"
              required
              value={form.legal_name}
              onChange={update('legal_name')}
              autoComplete="organization"
            />
          </label>

          <div className="invmodal__row">
            <label className="invmodal__field">
              <span>RFC</span>
              <input
                type="text"
                required
                value={form.tax_id}
                onChange={update('tax_id')}
                maxLength={13}
                style={{ textTransform: 'uppercase' }}
              />
            </label>
            <label className="invmodal__field">
              <span>Código postal</span>
              <input
                type="text"
                required
                value={form.zip}
                onChange={update('zip')}
                inputMode="numeric"
                maxLength={5}
                autoComplete="postal-code"
              />
            </label>
          </div>

          <label className="invmodal__field">
            <span>Email para envío</span>
            <input
              type="email"
              value={form.email}
              onChange={update('email')}
              autoComplete="email"
            />
          </label>

          <label className="invmodal__field">
            <span>Régimen fiscal</span>
            <select value={form.tax_system} onChange={update('tax_system')}>
              {TAX_SYSTEMS.map((t) => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
          </label>

          <label className="invmodal__field">
            <span>Uso de CFDI</span>
            <select value={form.use} onChange={update('use')}>
              {CFDI_USES.map((u) => (
                <option key={u.code} value={u.code}>{u.label}</option>
              ))}
            </select>
          </label>

          {error && <p className="invmodal__error">{error}</p>}

          <div className="invmodal__actions">
            <button
              type="button"
              className="invmodal__btn invmodal__btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="invmodal__btn invmodal__btn--solid"
              disabled={submitting}
            >
              {submitting ? 'Enviando…' : 'Solicitar factura'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
